import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import type {
  ColDef,
  ColumnGroupOpenedEvent,
  ColumnMenuVisibleChangedEvent,
  GetContextMenuItemsParams,
  GetMainMenuItems,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  RowHeightParams,
  ProcessCellForExportParams,
} from 'ag-grid-community';
import CommonAgGrid, { bombyxAgTheme } from '../../../../Components/CommonAgGrid';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import { bulkSubmitDownload, getColourWays, getLegLength, getMainSamples, getSizes, isSameSupplierExist } from '../../store/DevelopmentOrder.action';
import { getActiveIncotermsDetails, getAllUnitDetails, getOtherSamplesDetails } from '../../../SystemMasters/Store/SystemMasters.action';
import { fetchSupplierComponents } from '../../../Supplier/store/Supplier.action';
import CellReactSelect from './cells/CellReactSelect';
import { buildColumnDefs, CORE_PRODUCT_GROUP_ID, EXPANDABLE_GROUP_IDS, REQUEST_SAMPLE_GROUP_ID } from './columnDefs';
import { makeCoreLine, makeGradingLine, makeRow } from './sampleData';
import { COLUMN_MENU_ICON } from './columnMenuIcons';
import { useMultiSubmitData } from './Hooks/useMultiSubmitData';
import { buildSubmitRequests, validateSubmit, INTERNAL_SUPPLIER_ID } from './submitPayload';
import { collectCostingErrors } from './costingPayload';
import type { CostingErrorMap } from './costingPayload';
import { buildPushPatch, hasGradingData, type GradingPushMode } from './pushData';
import GradingPushModal from './GradingPushModal';
import { resolveAutomationContext, resolveNextSampleContext, samplesOfGroup } from './sampleChain';
import { EMPTY_ROW_OPTIONS } from './types';
import type { ArrKey, BoolKey, CostingErrorField, MultiSubmitGridContext, MultiSubmitRow, OptionsKey, RowOptions, SelectOption } from './types';
import {
  BASE_ROW_HEIGHT,
  FilterField,
  GRADING_LINE_HEIGHT,
  GridContainer,
  GridScrollArea,
  LoadMoreBar,
  PageWrapper,
  Title,
  TitleActions,
  TitleBar,
  TitleGroup,
} from './MultiSubmit.styled';
import { useProductLandingPath } from '../../../../Components/CustomHooks/useProductLandingPath';
import { unstable_batchedUpdates as batchUpdates } from 'react-dom';
import { fillSpecOf, sampleTypeOptions, valueOf } from './cellFill';
import { copyFillValue } from '../../../../Components/BombiixAGGrid/fillClipboard';

const ConfirmationPopup = require('../../../../Components/ModalPopup/ConfirmationPopup').default as React.FC<{
  onOpen: boolean;
  onConfirm: () => void;
  title: string;
  message: string;
  toggle: () => void;
}>;

interface SupplierCheckResult {
  data?: {
    supplierData?: Array<{ _id: string; name: string; email?: string; companies?: { currency?: string } }>;
    doc?: Array<{ _id: string; number: string; supplierFactories?: string[] }>;
  };
}

/** One checkbox+multi-select field within a group: header/cell checkbox selects every option. */
interface GroupSelectField {
  checkedField: BoolKey;
  valueField: ArrKey;
  optionsKey: OptionsKey;
}

/**
 * A group header checkbox's "select all" definition: ticking it sets every plain checkbox in
 * `bools` and fills every multi-select in `selects` with all its options (unticking clears both).
 */
interface GroupSpec {
  bools: BoolKey[];
  selects: GroupSelectField[];
}

/** Header-checkbox groups keyed by column-group id. Colourway/CP are single-select; TP/SD mix plain + multi-select fields. */
const GROUP_SPECS: Record<string, GroupSpec> = {
  colourway: {
    bools: [],
    selects: [{ checkedField: 'cwChecked', valueField: 'productColourways', optionsKey: 'colourwayOptions' }],
  },
  cp: {
    bools: [],
    selects: [{ checkedField: 'cpChecked', valueField: 'purchaseOrders', optionsKey: 'poOptions' }],
  },
  tp: {
    bools: ['tpDesignDetails', 'tpBom', 'tpTesting', 'tpCare'],
    selects: [
      { checkedField: 'tpSpecification', valueField: 'tpSpecificationValue', optionsKey: 'gradingGroups' },
      { checkedField: 'tpSample', valueField: 'tpSampleValue', optionsKey: 'sampleOptions' },
    ],
  },
  sd: {
    bools: ['sdTestingReport', 'sdCareLayout'],
    selects: [
      { checkedField: 'sdDesignDetails', valueField: 'sdDesignDetailsValue', optionsKey: 'designDocs' },
      { checkedField: 'sdColour', valueField: 'sdColourValue', optionsKey: 'colourDocs' },
      { checkedField: 'sdBom', valueField: 'sdBomValue', optionsKey: 'bomDocs' },
      { checkedField: 'sdSpecification', valueField: 'sdSpecificationValue', optionsKey: 'otherDocs' },
      { checkedField: 'sdPomGuide', valueField: 'sdPomGuideValue', optionsKey: 'gradingGroups' },
      { checkedField: 'sdPurchaseOrder', valueField: 'sdPurchaseOrderValue', optionsKey: 'poDocs' },
    ],
  },
};

/**
 * True when every product satisfies `spec`: all plain checkboxes ticked, every select field's
 * checkbox ticked, and every multi-select fully selected. A select field with no options for a
 * product counts as satisfied once its checkbox is ticked. Any group with a bool or select field
 * provides a checkbox signal, so the header reflects the per-row checkbox state even when a
 * product has no options (e.g. a product with no colourways).
 */
function allGroupChosen(rows: MultiSubmitRow[], optionsByRow: Record<string, RowOptions>, spec: GroupSpec): boolean {
  if (rows.length === 0) return false;
  if (spec.bools.length === 0 && spec.selects.length === 0) return false;
  return rows.every((row) => {
    if (!spec.bools.every((boolField) => row[boolField])) return false;
    return spec.selects.every((selectField) => {
      if (!row[selectField.checkedField]) return false;
      const availableOptions = (optionsByRow[row.id]?.[selectField.optionsKey] ?? []) as SelectOption[];
      if (availableOptions.length === 0) return true;
      const selectedOptions = row[selectField.valueField];
      return availableOptions.every((option) => selectedOptions.some((selected) => selected.value === option.value));
    });
  });
}

/** Rows revealed per infinite-scroll chunk; also the initial window size. */
const CHUNK_SIZE = 50;

/** Stable empty list so rows with no errors keep the same reference between renders. */
const EMPTY_ERRORS: CostingErrorField[] = [];

/** Keys of a patch whose value is a non-blank string — i.e. gaps the user has just closed. */
function filledKeysOf(patch: Record<string, unknown>): string[] {
  return Object.keys(patch).filter((key) => typeof patch[key] === 'string' && (patch[key] as string).trim() !== '');
}

/** Multi Submit — static AG Grid replica of the Figma design (local state only). */
const MultiSubmit: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  const { t } = useTranslation('modules/multi-submit');
  const { t: tCommon } = useTranslation('common');
  const productLandingPath = useProductLandingPath();
  const gridApiRef = useRef<GridApi<MultiSubmitRow> | null>(null);

  const [rows, setRows] = useState<MultiSubmitRow[]>([]);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sourceRowId, setSourceRowId] = useState<string | null>(null);
  const [showGradingPopup, setShowGradingPopup] = useState(false);
  const [isRequestSampleOpen, setIsRequestSampleOpen] = useState(false);
  const [isCoreProductOpen, setIsCoreProductOpen] = useState(false);
  const [heightVersion, setHeightVersion] = useState(0);
  const [groupChecks, setGroupChecks] = useState<Record<string, boolean>>({});
  const [supplierOptions, setSupplierOptions] = useState<SelectOption[]>([]);
  const [supplierList, setSupplierList] = useState<Array<{ _id: string; name: string; email?: string; companies?: { currency?: string } }>>([]);
  const supplierCurrencyById = useMemo(() => Object.fromEntries(supplierList.map((s) => [s._id, s.companies?.currency ?? ''])), [supplierList]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  /** Costing Information supplier is locked to the one selected header supplier — offer only that option. */
  const costingSupplierOptions = useMemo(() => supplierOptions.filter((option) => option.value === selectedSupplierId), [supplierOptions, selectedSupplierId]);
  const headerSupplierOptions = useMemo<SelectOption[]>(() => [{ value: INTERNAL_SUPPLIER_ID, label: 'Internal' }, ...supplierOptions], [supplierOptions]);
  const [supplierError, setSupplierError] = useState(false);
  const [rowErrors, setRowErrors] = useState<CostingErrorMap>({});
  /** Disables the Submit button while a submit is in flight. The ref guards against a rapid double-click
   *  landing two submits before the disabled state re-renders (a state flag alone can miss same-tick clicks). */
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const prevSupplierIdRef = useRef<string>('');
  const [factoryOptions, setFactoryOptions] = useState<SelectOption[]>([]);
  /** devOrderId -> the product's selected factory ids (DevelopmentOrders.supplierFactories). Scopes each
   *  product's Costing Information Factory dropdown to its own factories rather than every supplier factory. */
  const [factoryIdsByRow, setFactoryIdsByRow] = useState<Record<string, string[]>>({});
  const [visibleCount, setVisibleCount] = useState<number>(CHUNK_SIZE);
  const visibleCountRef = useRef<number>(CHUNK_SIZE);
  useEffect(() => {
    visibleCountRef.current = visibleCount;
  }, [visibleCount]);
  const userId = useSelector((state: { UserDetails?: { userInfo?: { userId?: string } } }) => state.UserDetails?.userInfo?.userId);
  const companyCurrency = useSelector(
    (state: { UserDetails?: { userInfo?: { companyCurrency?: string } } }) => state.UserDetails?.userInfo?.companyCurrency ?? '',
  );
  type RawMaster = { _id: string; name: string };
  /** Incoterms master rows carry the tenant's default flag (`isActiveIncoterms`), like the add page reads. */
  type RawIncoterm = RawMaster & { isActiveIncoterms?: boolean };
  const otherSamplesListRaw = useSelector(
    (state: { SystemMastersDetails?: { otherSamplesList?: RawMaster[] } }) => state.SystemMastersDetails?.otherSamplesList ?? [],
  );
  const mainSamplesListRaw = useSelector((state: { Measuremets?: { mainSamples?: RawMaster[] } }) => state.Measuremets?.mainSamples ?? []);
  const unitListRaw = useSelector((state: { SystemMastersDetails?: { unitList?: RawMaster[] } }) => state.SystemMastersDetails?.unitList ?? []);
  const sampleSizesRaw = useSelector((state: { Measuremets?: { sampleSizes?: Array<RawMaster | string> } }) => state.Measuremets?.sampleSizes ?? []);
  const sampleLengthRaw = useSelector((state: { Measuremets?: { sampleLength?: RawMaster[] } }) => state.Measuremets?.sampleLength ?? []);
  const incotermsListRaw = useSelector(
    (state: { SystemMastersDetails?: { activeIncotermsList?: RawIncoterm[] } }) => state.SystemMastersDetails?.activeIncotermsList ?? [],
  );

  const toSelectOptions = (masters: RawMaster[]): SelectOption[] => masters.map((master) => ({ value: master._id, label: master.name }));
  const toSelectOptionsFlexible = (masters: Array<RawMaster | string>): SelectOption[] =>
    masters.map((master) => (typeof master === 'string' ? { value: master, label: master } : { value: master._id, label: master.name }));
  const otherSamplesList = useMemo(() => otherSamplesListRaw, [otherSamplesListRaw]);
  const mainSamplesList = useMemo(() => mainSamplesListRaw, [mainSamplesListRaw]);
  const unitOptions = useMemo(() => toSelectOptions(unitListRaw), [unitListRaw]);
  const sizeOptions = useMemo(() => toSelectOptionsFlexible(sampleSizesRaw), [sampleSizesRaw]);
  const lengthOptions = useMemo(() => toSelectOptions(sampleLengthRaw), [sampleLengthRaw]);
  const incotermsOptions = useMemo(() => toSelectOptions(incotermsListRaw), [incotermsListRaw]);
  /* The tenant's default Incoterm — same flag product-costing-detail/add auto-selects on. */
  const defaultIncotermId = useMemo(() => incotermsListRaw.find((incoterm) => incoterm.isActiveIncoterms === true)?._id ?? '', [incotermsListRaw]);

  /* True while any row still lacks an Incoterm. A boolean (not the row array) keeps the effect below
     from re-running on unrelated cell edits, and flips back to true if rows reload blank. */
  const needsDefaultIncoterm = useMemo(() => rows.some((r) => !r.ciIncoterms), [rows]);

  /* Pre-select the default Incoterm wherever a row has none, leaving any user choice untouched. Keyed on
     the emptiness of the value like product-costing-detail/add, because the rows and the Incoterms master
     resolve independently — whichever lands last must still apply the default. */
  useEffect(() => {
    if (!defaultIncotermId || !needsDefaultIncoterm) return;
    setRows((prev) => prev.map((r) => (r.ciIncoterms ? r : { ...r, ciIncoterms: defaultIncotermId })));
  }, [defaultIncotermId, needsDefaultIncoterm]);

  useEffect(() => {
    if (!userId) return;
    dispatch(getMainSamples({ loggedInUserId: userId, isActive: true }));
    dispatch(getOtherSamplesDetails({ loggedInUserId: userId, isActive: true }));
    dispatch(getAllUnitDetails({ loggedInUserId: userId, isActive: true }));
    dispatch(getActiveIncotermsDetails({ isActive: true }));
  }, [dispatch, userId]);

  const firstDevOrderId = rows[0]?.id;
  useEffect(() => {
    if (!firstDevOrderId) return;
    dispatch(getColourWays({ devOrderId: firstDevOrderId }));
    dispatch(getSizes({ devOrderId: firstDevOrderId }));
    dispatch(getLegLength({ devOrderId: firstDevOrderId }));
  }, [dispatch, firstDevOrderId]);

  useEffect(() => {
    if (!selectedSupplierId) {
      setFactoryOptions([]);
      return;
    }
    let active = true;
    (async () => {
      const res = (await dispatch(fetchSupplierComponents({ pageSize: 20, pageIndex: 1, supplierId: selectedSupplierId }))) as
        | { doc?: Array<{ factoryId?: string; _id?: string; factoryName?: string; name?: string }> }
        | undefined;
      if (!active) return;
      const docs = res?.doc ?? [];
      setFactoryOptions(
        docs
          .map((factory) => ({
            value: factory.factoryId ?? factory._id ?? '',
            label: factory.factoryName ?? factory.name ?? '',
          }))
          .filter((option) => option.value !== ''),
      );
    })();
    return () => {
      active = false;
    };
  }, [dispatch, selectedSupplierId]);

  useEffect(() => {
    const idsParam = new URLSearchParams(location.search).get('ids');
    const devOrderIds = idsParam ? idsParam.split(',').filter(Boolean) : [];
    if (devOrderIds.length === 0) {
      history.replace('/development-order-overview');
      return undefined;
    }
    let active = true;
    (async () => {
      const res = (await dispatch(isSameSupplierExist({ devOrderIds }))) as SupplierCheckResult;
      if (!active) return;
      const supplierData = res?.data?.supplierData;
      if (!supplierData || supplierData.length === 0) {
        const key =
          devOrderIds.length === 1
            ? 'DEVLOPMENT_ORDER.PRODUCT_INFORMATIONS.SUBMIT_POPUP.SUBMIT_DOWNLOAD.SUPLIER_NOT_EXIST'
            : 'DEVLOPMENT_ORDER.PRODUCT_INFORMATIONS.SUBMIT_POPUP.SUBMIT_DOWNLOAD.SUPLIER_SAME_EXIST';
        toasterMsg(tCommon(key), 'danger');
        history.replace('/development-order-overview');
        return;
      }
      setSupplierList([{ _id: INTERNAL_SUPPLIER_ID, name: 'Internal', email: '' }, ...supplierData]);
      setSupplierOptions(supplierData.map((supplier) => ({ value: supplier._id, label: supplier.name })));
      const autoSupplier = supplierData.length === 1 ? supplierData[0] : undefined;
      if (autoSupplier) {
        setSelectedSupplierId(autoSupplier._id ?? '');
        prevSupplierIdRef.current = autoSupplier._id ?? '';
      }
      const autoEmail = autoSupplier?.email ? `${autoSupplier.email}, ` : '';
      const docs = res?.data?.doc || [];
      setFactoryIdsByRow(Object.fromEntries(docs.map((d) => [d._id, Array.isArray(d.supplierFactories) ? d.supplierFactories.map(String) : []])));
      setVisibleCount(CHUNK_SIZE);
      setRows(
        docs.map((devOrder) => {
          const row = makeRow(devOrder._id, devOrder.number);
          return autoEmail ? { ...row, emailTo: autoEmail } : row;
        }),
      );
    })();
    return () => {
      active = false;
    };
  }, [dispatch, history, location.search, tCommon]);

  const rowsRef = useRef<MultiSubmitRow[]>(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  /**
   * Product Number sorting runs over the FULL row set (all ids are in state from load —
   * there is no server pagination, only per-row options load lazily), then the window is
   * sliced. This way infinite-scroll reveals more of the already-sorted list instead of
   * AG Grid re-sorting a growing insertion-order window on every chunk.
   */
  const sortedRows = useMemo(() => {
    if (!sortDir) return rows;
    const direction = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((rowA, rowB) => rowA.productNumber.localeCompare(rowB.productNumber, undefined, { numeric: true, sensitivity: 'base' }) * direction);
  }, [rows, sortDir]);
  const pageRows = useMemo(() => sortedRows.slice(0, visibleCount), [sortedRows, visibleCount]);
  const pageIds = useMemo(() => pageRows.map((row) => row.id), [pageRows]);

  const pageIdSet = useMemo(() => new Set(pageIds), [pageIds]);
  const pageIdSetRef = useRef<Set<string>>(pageIdSet);
  useEffect(() => {
    pageIdSetRef.current = pageIdSet;
  }, [pageIdSet]);

  const sourceRowIdRef = useRef<string | null>(sourceRowId);
  useEffect(() => {
    sourceRowIdRef.current = sourceRowId;
  }, [sourceRowId]);

  const { optionsByRow: baseOptionsByRow, trackingDates, userMentions, loading } = useMultiSubmitData(pageIds, selectedSupplierId || null);

  /* Resolve each product's Costing Information factory options here (not in the data hook) because only the
     page holds both the supplier factory master (`factoryOptions`) and the product's selected factory ids.
     A product with no selected factory keeps an empty list, so its Factory dropdown offers nothing. */
  const optionsByRow = useMemo(() => {
    const merged: Record<string, RowOptions> = {};
    for (const [rowId, rowOptions] of Object.entries(baseOptionsByRow)) {
      const idSet = new Set(factoryIdsByRow[rowId] ?? []);
      merged[rowId] = {
        ...rowOptions,
        ciFactoryOptions: idSet.size ? factoryOptions.filter((option) => idSet.has(option.value)) : [],
      };
    }
    return merged;
  }, [baseOptionsByRow, factoryIdsByRow, factoryOptions]);

  const optionsByRowRef = useRef(optionsByRow);
  useEffect(() => {
    optionsByRowRef.current = optionsByRow;
  }, [optionsByRow]);

  const groupSelectAll = useMemo(() => {
    const out: Record<string, boolean> = {};
    Object.entries(GROUP_SPECS).forEach(([gid, spec]) => {
      out[gid] = allGroupChosen(pageRows, optionsByRow, spec);
    });
    return out;
  }, [pageRows, optionsByRow]);
  useEffect(() => {
    setGroupChecks((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.keys(GROUP_SPECS).forEach((gid) => {
        if ((prev[gid] ?? false) !== groupSelectAll[gid]) {
          next[gid] = groupSelectAll[gid] ?? false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [groupSelectAll]);

  /**
   * A group header "select all" ticks every row, but rows in not-yet-loaded chunks
   * have no options at click time, so their multi-select value fields stay empty.
   * When a later chunk's options arrive, fill any select field that is checked but
   * still empty, so scrolling down reveals fully-selected rows (not just the first chunk).
   */
  useEffect(() => {
    setRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        const rowOptions = optionsByRow[row.id];
        if (!rowOptions) return row;
        let patch: Record<string, unknown> | null = null;
        Object.values(GROUP_SPECS).forEach((spec) => {
          spec.selects.forEach((selectField) => {
            const available = rowOptions[selectField.optionsKey] as SelectOption[] | undefined;
            if (row[selectField.checkedField] && available && available.length > 0 && (row[selectField.valueField] as SelectOption[]).length === 0) {
              patch = patch ?? {};
              patch[selectField.valueField] = available;
            }
          });
        });
        if (patch) {
          changed = true;
          return { ...row, ...(patch as Partial<MultiSubmitRow>) };
        }
        return row;
      });
      return changed ? next : prev;
    });
  }, [optionsByRow]);

  /* Recomputes whenever any row's Costing Information supplier changes, so the auto-select effect
     below re-runs the moment a row opts into costing (setting ciSupplier does not change optionsByRow). */
  const costingSupplierSignal = useMemo(() => rows.map((r) => `${r.id}:${r.ciSupplier}`).join(','), [rows]);

  /**
   * Auto-select the Costing Information template and factory on a row once its Costing Information
   * supplier is chosen — the moment the row opts into costing — when exactly one option exists and none
   * is picked yet. Rows without a costing supplier are left untouched, so nothing auto-fills merely from
   * the header supplier selection. A user's own pick is never overwritten (a non-empty value is skipped).
   */
  useEffect(() => {
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        if (!r.ciSupplier) return r;
        const opts = optionsByRow[r.id];
        if (!opts) return r;
        const patch: Partial<MultiSubmitRow> = {};
        const [onlyTemplate] = opts.costingTemplateOptions;
        if (!r.ciCostingTemplate && opts.costingTemplateOptions.length === 1 && onlyTemplate) {
          patch.ciCostingTemplate = onlyTemplate.value;
        }
        const [onlyFactory] = opts.ciFactoryOptions;
        if (!r.ciFactory && opts.ciFactoryOptions.length === 1 && onlyFactory) {
          patch.ciFactory = onlyFactory.value;
        }
        if (Object.keys(patch).length === 0) return r;
        changed = true;
        return { ...r, ...patch };
      });
      return changed ? next : prev;
    });
  }, [optionsByRow, costingSupplierSignal]);

  const onSupplierChange = useCallback(
    (id: string) => {
      setSelectedSupplierId(id);
      if (id) setSupplierError(false);
      const isInternal = id === INTERNAL_SUPPLIER_ID;
      const email = supplierList.find((supplier) => supplier._id === id)?.email;
      const prevEmail = supplierList.find((supplier) => supplier._id === prevSupplierIdRef.current)?.email;
      prevSupplierIdRef.current = id;
      const prevAutoFilled = prevEmail ? `${prevEmail}, ` : '';
      setRows((prev) =>
        prev.map((r) => {
          const next = { ...r };
          /* Costing supplier is locked to the header supplier: retarget any row that opted into
             costing, and clear it (costing off) when the header supplier is cleared. */
          if (r.ciSupplier) next.ciSupplier = id;
          /* Internal has no supplier email — clear the previous supplier's auto-filled "To" so the
             user enters recipients manually, but never clobber a user-typed address. */
          if (isInternal) {
            if (!r.emailTo || r.emailTo === prevAutoFilled) next.emailTo = '';
          } else if (email && (!r.emailTo || r.emailTo === prevAutoFilled)) {
            /* Auto-fill the Compose "To" from the supplier email only while it is empty or still holds
               the previous supplier's auto-filled value — never clobber a user-typed address. */
            next.emailTo = `${email}, `;
          }
          /* Sample options are supplier-scoped, so a selection made under the previous supplier is no
             longer valid — reset both the Technical Package sample field and the Request Sample group
             so the row does not keep a stale value the new supplier's option list cannot show. */
          next.tpSample = false;
          next.tpSampleValue = [];
          next.rsChecked = false;
          next.sampleTypeGroup = '';
          next.typeOfSampleType = '';
          next.sampleType = '';
          next.sampleNumber = '';
          next.rsSupplier = '';
          next.dueDate = '';
          next.factory = '';
          next.gradingLines = [makeGradingLine()];
          next.parentId = null;
          next.revisionRootId = null;
          next.versionNumber = 1;
          return next;
        }),
      );
      /* Clearing the header supplier turns costing off for every row, so drop their costing gaps. */
      if (!id) setRowErrors({});
    },
    [supplierList],
  );

  const bumpHeight = useCallback(() => setHeightVersion((version) => version + 1), []);

  const setAllGroupsExpanded = useCallback((open: boolean) => {
    const api = gridApiRef.current;
    if (!api) return;
    EXPANDABLE_GROUP_IDS.forEach((gid) => api.setColumnGroupOpened(gid, open));
  }, []);

  /** Drop the named fields from a row's error list, removing the row once it is clean. */
  const dropRowErrors = useCallback((rowId: string, fields: string[]) => {
    setRowErrors((prev) => {
      const current = prev[rowId];
      if (!current) return prev;
      const remaining = current.filter((field) => !fields.includes(field));
      const next = { ...prev };
      if (remaining.length > 0) next[rowId] = remaining as CostingErrorField[];
      else delete next[rowId];
      return next;
    });
  }, []);

  const updateRow = useCallback(
    (id: string, patch: Partial<MultiSubmitRow>) => {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
      /* Clearing the supplier turns costing off for the row, so its gaps stop being gaps —
         otherwise a red cell would strand behind a disabled control. */
      if ('ciSupplier' in patch && !patch.ciSupplier) {
        setRowErrors((prev) => {
          if (!prev[id]) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
        return;
      }
      const filled = filledKeysOf(patch as Record<string, unknown>);
      if (filled.length > 0) dropRowErrors(id, filled);
    },
    [dropRowErrors],
  );

  const updateGradingLine = useCallback((rowId: string, lineId: string, patch: Partial<MultiSubmitRow['gradingLines'][number]>) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, gradingLines: r.gradingLines.map((l) => (l.id === lineId ? { ...l, ...patch } : l)) } : r)));
  }, []);

  const addGradingLine = useCallback(
    (rowId: string) => {
      setRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, gradingLines: [...row.gradingLines, makeGradingLine()], expanded: true } : row)));
      bumpHeight();
    },
    [bumpHeight],
  );

  const removeGradingLine = useCallback(
    (rowId: string, lineId: string) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== rowId || r.gradingLines.length <= 1) return r;
          const gradingLines = r.gradingLines.filter((l) => l.id !== lineId);
          return { ...r, gradingLines, expanded: gradingLines.length > 1 || r.coreLines.length > 1 ? r.expanded : false };
        }),
      );
      bumpHeight();
    },
    [bumpHeight],
  );

  const updateCoreLine = useCallback(
    (rowId: string, lineId: string, patch: Partial<MultiSubmitRow['coreLines'][number]>) => {
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, coreLines: r.coreLines.map((l) => (l.id === lineId ? { ...l, ...patch } : l)) } : r)));
      const filled = filledKeysOf(patch as Record<string, unknown>).map((key) => `core.${key}`);
      if (filled.length > 0) dropRowErrors(rowId, filled);
    },
    [dropRowErrors],
  );

  const addCoreLine = useCallback(
    (rowId: string) => {
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, coreLines: [...r.coreLines, makeCoreLine()], expanded: true } : r)));
      bumpHeight();
    },
    [bumpHeight],
  );

  const toggleRowExpanded = useCallback(
    (id: string) => {
      const target = rowsRef.current.find((row) => row.id === id);
      const willExpand = target ? !target.expanded : false;
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, expanded: !row.expanded } : row)));
      if (willExpand) setAllGroupsExpanded(true);
      bumpHeight();
    },
    [bumpHeight, setAllGroupsExpanded],
  );

  const isRowExpandable = useCallback((r: MultiSubmitRow) => r.gradingLines.length > 1 || r.coreLines.length > 1, []);
  const anyRowExpandable = useMemo(() => rows.some(isRowExpandable), [rows, isRowExpandable]);
  const allRowsExpanded = useMemo(() => anyRowExpandable && rows.filter(isRowExpandable).every((r) => r.expanded), [rows, anyRowExpandable, isRowExpandable]);

  const toggleAllRowsExpanded = useCallback(() => {
    const expandable = rowsRef.current.filter(isRowExpandable);
    if (expandable.length === 0) return;
    const open = !expandable.every((r) => r.expanded);
    setRows((prev) => prev.map((r) => (isRowExpandable(r) ? { ...r, expanded: open } : r)));
    setAllGroupsExpanded(open);
    bumpHeight();
  }, [bumpHeight, setAllGroupsExpanded, isRowExpandable]);

  const deleteSelected = useCallback(() => {
    setRows((prev) => prev.filter((row) => !row.selected));
    setShowDeleteConfirm(false);
  }, []);

  const setSourceRow = useCallback((id: string) => {
    setSourceRowId((prev) => (prev === id ? null : id));
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, selected: false } : row)));
  }, []);

  const openProductDetail = useCallback(
    (id: string) => {
      window.open(productLandingPath(id), '_blank', 'noopener');
    },
    [productLandingPath],
  );

  const columnMenuOpenRef = useRef(false);
  const columnMenuClosedAtRef = useRef(0);
  const onColumnMenuVisibleChanged = useCallback((event: ColumnMenuVisibleChangedEvent<MultiSubmitRow>) => {
    columnMenuOpenRef.current = event.visible;
    if (!event.visible) columnMenuClosedAtRef.current = performance.now();
  }, []);

  /**
   * Toggle the Product Number column menu. When the menu is open, AG Grid's own outside-click
   * handler closes it on the same click — the recently-closed guard absorbs that so the button
   * doesn't immediately reopen it.
   */
  const toggleProductColumnMenu = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const justClosed = performance.now() - columnMenuClosedAtRef.current < 250;
    if (columnMenuOpenRef.current || justClosed) {
      api.hidePopupMenu();
      return;
    }
    api.showColumnMenu('product-number');
  }, []);

  const runPush = useCallback(
    (mode: GradingPushMode) => {
      const source = rowsRef.current.find((row) => row.id === sourceRowId);
      if (!source) return;
      const targetCount = rowsRef.current.filter((row) => row.selected && row.id !== source.id).length;
      if (targetCount === 0) return;
      setRows((prev) =>
        prev.map((row) => {
          if (!row.selected || row.id === source.id) return row;
          const patch = buildPushPatch(source, row, optionsByRowRef.current[row.id] ?? EMPTY_ROW_OPTIONS, mode);
          return { ...row, ...patch };
        }),
      );
      setShowGradingPopup(false);
      bumpHeight();
      toasterMsg(t('PUSH.SUCCESS', { count: targetCount }), 'success');
    },
    [sourceRowId, bumpHeight, t],
  );

  const onPushClick = useCallback(() => {
    const source = rowsRef.current.find((row) => row.id === sourceRowId);
    if (!source) return;
    if (hasGradingData(source)) setShowGradingPopup(true);
    else runPush('blank');
  }, [sourceRowId, runPush]);

  const toggleSelectAll = useCallback((checked: boolean) => {
    const srcId = sourceRowIdRef.current;
    setRows((prev) => prev.map((row) => (row.id === srcId ? row : { ...row, selected: checked })));
  }, []);

  const toggleGroupCheck = useCallback((groupId: string) => {
    const spec = GROUP_SPECS[groupId];
    if (!spec) {
      setGroupChecks((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
      return;
    }
    const pageSet = pageIdSetRef.current;
    const pageRowsNow = rowsRef.current.filter((row) => pageSet.has(row.id));
    const next = !allGroupChosen(pageRowsNow, optionsByRowRef.current, spec);
    /**
     * Apply to EVERY row (all ids live in state from load; only options are chunk-lazy).
     * Bool/checkbox fields set immediately for all rows. Select values fill from the
     * row's options where already loaded; rows in not-yet-loaded chunks fill later via
     * the options-reconcile effect keyed on optionsByRow.
     */
    setRows((prev) =>
      prev.map((row) => {
        const patch: Record<string, unknown> = {};
        spec.bools.forEach((boolField) => {
          patch[boolField] = next;
        });
        spec.selects.forEach((selectField) => {
          patch[selectField.checkedField] = next;
          const availableOptions = optionsByRowRef.current[row.id]?.[selectField.optionsKey];
          patch[selectField.valueField] = next ? availableOptions ?? row[selectField.valueField] : [];
        });
        return { ...row, ...(patch as Partial<MultiSubmitRow>) };
      }),
    );
    setGroupChecks((prev) => ({ ...prev, [groupId]: next }));
  }, []);

  const allSelected = pageRows.length > 0 && pageRows.every((row) => row.selected);
  const selectedCount = useMemo(() => rows.filter((row) => row.selected).length, [rows]);

  const setSampleTypeGroup = useCallback(
    (rowId: string, groupId: string) => {
      const groups = optionsByRowRef.current[rowId]?.sampleTypeGroupRaw ?? [];
      const group = groups.find((sampleGroup) => sampleGroup._id === groupId);
      const firstAutomation = group?.sampleTypeAutomation?.[0];
      const isAutomation = (firstAutomation as { configuration?: unknown[] })?.configuration?.length
        ? ((firstAutomation as { configuration?: unknown[] }).configuration as unknown[]).length > 0
        : false;
      const samplesList = optionsByRowRef.current[rowId]?.samplesList ?? [];
      const auto =
        isAutomation && group
          ? resolveAutomationContext(group, samplesList)
          : { sampleType: '', parentId: null, revisionRootId: null, versionNumber: 1, supplier: '' };
      const fallbackSupplier = supplierOptions[0]?.value ?? '';
      const chainSupplier = supplierOptions.some((option) => option.value === auto.supplier) ? auto.supplier : '';
      const autoSupplier = isAutomation && !auto.supplier ? fallbackSupplier : chainSupplier;
      setRows((prev) =>
        prev.map((row) =>
          row.id === rowId
            ? {
                ...row,
                sampleTypeGroup: groupId,
                typeOfSampleType: groupId ? 'main' : '',
                sampleType: auto.sampleType,
                rsSupplier: autoSupplier,
                parentId: auto.parentId,
                revisionRootId: auto.revisionRootId,
                versionNumber: auto.versionNumber,
                rsChecked: !!groupId || row.rsChecked,
              }
            : row,
        ),
      );
    },
    [supplierOptions],
  );

  /**
   * Sample Type picked by the user. The chain attachment is recomputed for the chosen type, and the
   * supplier follows that chain when the type continues one — otherwise the user's own pick stands.
   *
   * The candidate samples are scoped to the row's selected Sample Type Group: a sample of the same
   * type in a DIFFERENT group is a different chain, and versioning onto it would file the new
   * request under that other group on the Samples page.
   */
  const setSampleType = useCallback((rowId: string, sampleTypeId: string) => {
    setRows((prev) => prev.map((row) => (row.id === rowId ? withSampleType(row, sampleTypeId, optionsByRowRef.current[rowId], supplierOptions) : row)));
  }, [supplierOptions]);

  const pendingCellWritesRef = useRef<Array<{ rowId: string; write: () => void }>>([]);

  /**
   * Applies every fill/paste write queued in this task in one batched render. The app mounts with
   * legacy ReactDOM.render, which does not batch updates fired from AG Grid's own listeners.
   */
  const flushCellWrites = useCallback(() => {
    const writes = pendingCellWritesRef.current;
    pendingCellWritesRef.current = [];
    batchUpdates(() => writes.forEach((pending) => pending.write()));
  }, []);

  /**
   * Queues one cell write from a column's valueSetter; the first write of a task schedules the flush.
   * @param rowId - the row being written.
   * @param write - the write, run inside the batched flush.
   */
  const queueCellWrite = useCallback(
    (rowId: string, write: () => void) => {
      pendingCellWritesRef.current.push({ rowId, write });
      if (pendingCellWritesRef.current.length === 1) queueMicrotask(flushCellWrites);
    },
    [flushCellWrites],
  );

  /**
   * Expands a product row, opening the column groups as the row caret does. Unlike the caret it never
   * collapses, so several fills reaching the same row in one paste all leave it open.
   * @param id - the row to expand.
   */
  const expandRow = useCallback(
    (id: string) => {
      setRows((prev) => prev.map((row) => (row.id === id && !row.expanded ? { ...row, expanded: true } : row)));
      setAllGroupsExpanded(true);
      bumpHeight();
    },
    [bumpHeight, setAllGroupsExpanded],
  );

  /**
   * Sets a row's Sample Type from a fill or paste. The id or label is resolved inside the state update,
   * against the row's Sample Type Group as earlier writes of the same paste left it; a type that group
   * does not offer, or the type the row already has, leaves the row as it is.
   * @param rowId - the row being written.
   * @param idOrLabel - the dropped Sample Type id, or the pasted label.
   */
  const fillSampleType = useCallback(
    (rowId: string, idOrLabel: string) => {
      const optionSource = { optionsFor: (id: string) => optionsByRowRef.current[id] ?? EMPTY_ROW_OPTIONS, otherSamplesList };
      setRows((prev) =>
        prev.map((row) => {
          if (row.id !== rowId) return row;
          const sampleTypeId = valueOf(sampleTypeOptions(row, optionSource), idOrLabel);
          return sampleTypeId && sampleTypeId !== row.sampleType ? withSampleType(row, sampleTypeId, optionsByRowRef.current[rowId], supplierOptions) : row;
        }),
      );
    },
    [otherSamplesList, supplierOptions],
  );

  const gridContext = useMemo<MultiSubmitGridContext>(
    () => ({
      updateRow,
      updateGradingLine,
      addGradingLine,
      removeGradingLine,
      updateCoreLine,
      addCoreLine,
      toggleRowExpanded,
      setAllGroupsExpanded,
      toggleAllRowsExpanded,
      allRowsExpanded,
      anyRowExpandable,
      toggleSelectAll,
      allSelected,
      sourceRowId,
      setSourceRow,
      openProductDetail,
      toggleProductColumnMenu,
      groupChecks,
      toggleGroupCheck,
      isRequestSampleOpen,
      isCoreProductOpen,
      optionsByRow,
      optionsFor: (rowId: string) => optionsByRow[rowId] ?? EMPTY_ROW_OPTIONS,
      errorsFor: (rowId: string) => rowErrors[rowId] ?? EMPTY_ERRORS,
      isRowInvited: (id: string) => rowsRef.current.find((r) => r.id === id)?.invite ?? false,
      t,
      selectedSupplierId,
      supplierOptions,
      costingSupplierOptions,
      companyCurrency,
      supplierCurrencyById,
      factoryOptions,
      unitOptions,
      mainSamplesList,
      otherSamplesList,
      sizeOptions,
      lengthOptions,
      incotermsOptions,
      setSampleTypeGroup,
      setSampleType,
      userMentions,
      queueCellWrite,
      expandRow,
      fillSampleType,
    }),
    [
      updateRow,
      updateGradingLine,
      addGradingLine,
      removeGradingLine,
      updateCoreLine,
      addCoreLine,
      toggleRowExpanded,
      setAllGroupsExpanded,
      toggleAllRowsExpanded,
      allRowsExpanded,
      anyRowExpandable,
      toggleSelectAll,
      allSelected,
      sourceRowId,
      setSourceRow,
      openProductDetail,
      toggleProductColumnMenu,
      groupChecks,
      toggleGroupCheck,
      isRequestSampleOpen,
      isCoreProductOpen,
      optionsByRow,
      rowErrors,
      t,
      selectedSupplierId,
      supplierOptions,
      costingSupplierOptions,
      companyCurrency,
      supplierCurrencyById,
      factoryOptions,
      unitOptions,
      mainSamplesList,
      otherSamplesList,
      sizeOptions,
      lengthOptions,
      incotermsOptions,
      setSampleTypeGroup,
      setSampleType,
      userMentions,
      queueCellWrite,
      expandRow,
      fillSampleType,
    ],
  );

  const columnDefs = useMemo(() => buildColumnDefs(t), [t]);
  const defaultColDef = useMemo<ColDef<MultiSubmitRow>>(
    () => ({
      resizable: true,
      sortable: false,
      suppressMovable: true,
      suppressKeyboardEvent: ({ event }) => isInsideCellControl(event.target) || isEditStartKey(event),
    }),
    [],
  );

  const gridTheme = useMemo(() => bombyxAgTheme.withParams({ headerBackgroundColor: '#f3f3f3' }), []);

  const getRowId = useCallback((params: GetRowIdParams<MultiSubmitRow>) => params.data.id, []);

  const getRowClass = useCallback(
    (params: RowClassParams<MultiSubmitRow>): string[] => {
      const classes: string[] = [];
      if (params.data?.id === sourceRowId) classes.push('mssg-row-source');
      if (params.data?.expanded) classes.push('mssg-row-expanded');
      return classes;
    },
    [sourceRowId],
  );

  /** Suppress AG Grid's right-click context menu on the Product Number column (keeps it elsewhere). */
  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<MultiSubmitRow>) => (params.column?.getColId() === 'product-number' ? [] : params.defaultItems ?? []),
    [],
  );

  /**
   * Column menu (three-dot) for the Product Number column only — styled + iconed to match
   * Figma (node 5124:195038): sort, pin (submenu), autosize, reset with exact Figma icons.
   * Every other column keeps AG Grid's default items (their leaf headers are hidden, so it never shows).
   * Sorts the full dataset in React state (see sortedRows) rather than AG Grid's window sort, so
   * infinite-scroll chunks don't re-shuffle the order.
   */
  const getMainMenuItems = useCallback<GetMainMenuItems<MultiSubmitRow>>(
    (params) => {
      if (params.column?.getColId() !== 'product-number') return [];
      const { api } = params;
      return [
        { name: t('MENU.SORT_ASC'), icon: COLUMN_MENU_ICON.sortAscending, action: () => setSortDir('asc') },
        { name: t('MENU.SORT_DESC'), icon: COLUMN_MENU_ICON.sortDescending, action: () => setSortDir('desc') },
        'separator',
        {
          name: t('MENU.PIN'),
          icon: COLUMN_MENU_ICON.pin,
          subMenu: [
            { name: t('MENU.PIN_LEFT'), action: () => api.setColumnsPinned(['product-number'], 'left') },
            { name: t('MENU.PIN_RIGHT'), action: () => api.setColumnsPinned(['product-number'], 'right') },
            { name: t('MENU.NO_PIN'), action: () => api.setColumnsPinned(['product-number'], null) },
          ],
        },
        'separator',
        { name: t('MENU.AUTOSIZE_THIS'), action: () => api.autoSizeColumns(['product-number']) },
        { name: t('MENU.AUTOSIZE_ALL'), action: () => api.autoSizeAllColumns() },
        'separator',
        {
          name: t('MENU.RESET'),
          action: () => {
            setSortDir(null);
            api.resetColumnState();
          },
        },
      ];
    },
    [t],
  );

  const getRowHeight = useCallback(
    (params: RowHeightParams<MultiSubmitRow>): number => {
      const row = params.data;
      if (row && row.expanded) {
        let lineCount = 1;
        if (isRequestSampleOpen) lineCount = Math.max(lineCount, row.gradingLines.length);
        if (isCoreProductOpen) lineCount = Math.max(lineCount, row.coreLines.length);
        if (lineCount > 1) return lineCount * GRADING_LINE_HEIGHT + 6;
      }
      return BASE_ROW_HEIGHT;
    },
    [isRequestSampleOpen, isCoreProductOpen],
  );

  const onGridReady = useCallback((event: GridReadyEvent<MultiSubmitRow>) => {
    gridApiRef.current = event.api;
  }, []);

  const onColumnGroupOpened = useCallback((e: ColumnGroupOpenedEvent<MultiSubmitRow>) => {
    const rs = e.columnGroups.find((g) => g.getGroupId() === REQUEST_SAMPLE_GROUP_ID);
    if (rs) setIsRequestSampleOpen(rs.isExpanded());
    const core = e.columnGroups.find((g) => g.getGroupId() === CORE_PRODUCT_GROUP_ID);
    if (core) setIsCoreProductOpen(core.isExpanded());
  }, []);

  /** Grow the revealed window by one chunk when the user scrolls near the last rendered row. */
  const maybeLoadMore = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const total = rowsRef.current.length;
    if (visibleCountRef.current >= total) return;
    let lastRendered = -1;
    api.getRenderedNodes().forEach((node) => {
      if (typeof node.rowIndex === 'number' && node.rowIndex > lastRendered) lastRendered = node.rowIndex;
    });
    if (lastRendered >= visibleCountRef.current - 10) {
      const next = Math.min(visibleCountRef.current + CHUNK_SIZE, total);
      visibleCountRef.current = next;
      setVisibleCount(next);
    }
  }, []);

  useEffect(() => {
    gridApiRef.current?.resetRowHeights();
  }, [heightVersion, isRequestSampleOpen, isCoreProductOpen]);

  useEffect(() => {
    gridApiRef.current?.refreshHeader();
  }, [allSelected, groupChecks, allRowsExpanded, anyRowExpandable, sourceRowId]);

  /** Ids of currently-expanded rows, so a toggle re-applies the .mssg-row-expanded row class. */
  const expandedRowKey = useMemo(
    () =>
      pageRows
        .filter((row) => row.expanded)
        .map((row) => row.id)
        .join(','),
    [pageRows],
  );

  /**
   * Ids of currently-selected rows. The row-select checkbox lives in the
   * product-number cell whose value is `productNumber`, so AG Grid's change
   * detection won't repaint it when only `selected` flips (e.g. select-all).
   * Force a refresh of that column whenever the selected set changes.
   */
  const selectedRowKey = useMemo(
    () =>
      pageRows
        .filter((row) => row.selected)
        .map((row) => row.id)
        .join(','),
    [pageRows],
  );

  useEffect(() => {
    gridApiRef.current?.redrawRows();
  }, [sourceRowId, expandedRowKey]);

  /**
   * The Costing Information currency display and the Core Product price symbol both derive from
   * each row's `ciSupplier` (via context lookup), and those cells carry no field value — so AG
   * Grid's change detection won't repaint them when only `ciSupplier` changes. Force a refresh of
   * exactly those columns whenever any row's Costing Information supplier changes.
   */
  const ciSupplierKey = useMemo(() => pageRows.map((r) => `${r.id}:${r.ciSupplier}`).join(','), [pageRows]);

  useEffect(() => {
    gridApiRef.current?.refreshCells({ columns: ['ci-currency', 'core-ppu', 'core-total'], force: true });
  }, [ciSupplierKey]);

  /**
   * The supplier-invite icon lives in the `core-total` cell but reflects each row's `invite` flag,
   * which is not that cell's value — so AG Grid's change detection won't repaint it (and turn it
   * green) when only `invite` toggles. Force a refresh of that column whenever any row's flag changes.
   */
  const inviteKey = useMemo(() => pageRows.map((r) => `${r.id}:${r.invite}`).join(','), [pageRows]);

  useEffect(() => {
    gridApiRef.current?.refreshCells({ columns: ['core-total'], force: true });
  }, [inviteKey]);

  useEffect(() => {
    gridApiRef.current?.refreshCells({ force: true, columns: ['product-number'] });
  }, [selectedRowKey]);

  /**
   * Group header checkboxes (Colourway / Costing / Technical Package / Supporting
   * Documents) bulk-set every cell in their group, but those cells render via a
   * cellRenderer with no column value, so AG Grid's change detection never
   * repaints them. Force a refresh whenever a group's select-all state changes.
   */
  useEffect(() => {
    gridApiRef.current?.refreshCells({ force: true });
  }, [groupChecks]);

  /**
   * The costing error cells render via `errorsFor`, a context-only accessor, so AG Grid's
   * change detection never sees `rowErrors` change. Force a refresh whenever it does.
   */
  useEffect(() => {
    gridApiRef.current?.refreshCells({ force: true });
  }, [rowErrors]);

  useEffect(() => {
    gridApiRef.current?.refreshCells({ force: true });
  }, [optionsByRow]);

  const handleSubmit = useCallback(async () => {
    /* Ignore repeat clicks while a submit is already running, so a double-click cannot queue two
       submit-download batches (and two costing requests) for the same selection. */
    if (submittingRef.current) return;
    if (!selectedSupplierId) {
      setSupplierError(true);
      return;
    }
    const result = validateSubmit(rows, selectedSupplierId);
    if (!result.ok) {
      toasterMsg(t(result.errorKey, { productNumber: result.productNumber ?? '' }), 'danger');
      return;
    }
    const costing = collectCostingErrors(rows, supplierCurrencyById);
    setRowErrors(costing.errors);
    if (costing.productCount > 0) {
      toasterMsg(t('VALIDATION.COSTING_FIELDS_REQUIRED', { count: costing.productCount }), 'danger');
      return;
    }
    if (costing.currencyMissingFor) {
      toasterMsg(t('VALIDATION.COSTING_CURRENCY_REQUIRED', { productNumber: costing.currencyMissingFor }), 'danger');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
      const requests = buildSubmitRequests(rows, { supplierId: selectedSupplierId, trackingDates, timeZone, supplierCurrencyById });
      const code = await dispatch(bulkSubmitDownload({ requests }));
      if (code === 200) history.replace('/development-order-overview');
    } catch (e) {
      console.error(e);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [rows, selectedSupplierId, trackingDates, dispatch, history, t, supplierCurrencyById]);

  /** Fill handle: drag down only, and always copy — a dragged `12` stays `12`, never a number series. */
  const cellSelection = useMemo(
    () => ({
      handle: {
        mode: 'fill' as const,
        direction: 'y' as const,
        setFillValue: copyFillValue,
      },
    }),
    [],
  );

  /** Clipboard copy writes what a cell shows (labels), never the ids it stores. */
  const processCellForClipboard = useCallback((params: ProcessCellForExportParams<MultiSubmitRow>) => {
    const spec = fillSpecOf(params.column.getColDef());
    const row = params.node?.data;
    return spec && row ? spec.toText(params.value, row, params.context) : params.value ?? '';
  }, []);

  /** Clipboard paste resolves labels against the target row's own options. */
  const processCellFromClipboard = useCallback((params: ProcessCellForExportParams<MultiSubmitRow>) => {
    const spec = fillSpecOf(params.column.getColDef());
    const row = params.node?.data;
    return spec && row ? spec.fromText(String(params.value ?? ''), row, params.context) : params.value;
  }, []);

  return (
    <PageWrapper>
      <TitleBar>
        <TitleGroup>
          <Title>{t('PAGE.TITLE')}</Title>
          <FilterField>
            <CellReactSelect
              value={selectedSupplierId}
              options={headerSupplierOptions}
              placeholder={supplierError ? t('PAGE.REQUIRED_FIELD') : t('PAGE.SUPPLIER')}
              onChange={onSupplierChange}
              error={supplierError}
              fontSize={14}
            />
          </FilterField>
        </TitleGroup>
        <TitleActions>
          <button type="button" className={`bombiix-btn btn-blue${submitting ? ' disabled' : ''}`} onClick={handleSubmit} disabled={submitting}>
            {t('PAGE.SUBMIT')}
          </button>
          <button type="button" className="bombiix-btn btn-light" onClick={() => history.goBack()}>
            {t('PAGE.BACK')}
          </button>
        </TitleActions>
      </TitleBar>

      <GridContainer $rowsDisabled={!selectedSupplierId}>
        <GridScrollArea>
          <CommonAgGrid<MultiSubmitRow>
            height="100%"
            domLayout="normal"
            theme={gridTheme}
            rowData={pageRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            context={gridContext}
            getRowId={getRowId}
            getRowClass={getRowClass}
            getRowHeight={getRowHeight}
            getContextMenuItems={getContextMenuItems}
            getMainMenuItems={getMainMenuItems}
            onColumnMenuVisibleChanged={onColumnMenuVisibleChanged}
            onGridReady={onGridReady}
            onColumnGroupOpened={onColumnGroupOpened}
            onBodyScroll={maybeLoadMore}
            onFirstDataRendered={maybeLoadMore}
            headerHeight={0}
            groupHeaderHeight={40}
            suppressColumnVirtualisation
            cellSelection={cellSelection}
            suppressClickEdit
            processCellForClipboard={processCellForClipboard}
            processCellFromClipboard={processCellFromClipboard}
          />
        </GridScrollArea>
        {visibleCount > CHUNK_SIZE && loading && <LoadMoreBar>{t('PAGE.LOADING_MORE')}</LoadMoreBar>}
      </GridContainer>

      <div className="floting-actions">
        <CustomTooltip placement="top" tooltipText={sourceRowId ? t('ACTIONS.PUSH') : t('PUSH.SELECT_SOURCE')}>
          <button type="button" aria-label={t('ACTIONS.PUSH')} className="btn" disabled={!sourceRowId} onClick={onPushClick}>
            <i className="icon-Push" />
          </button>
        </CustomTooltip>
        <CustomTooltip placement="top" tooltipText={t('ACTIONS.DELETE')}>
          <button type="button" aria-label={t('ACTIONS.DELETE')} className="btn" disabled={selectedCount === 0} onClick={() => setShowDeleteConfirm(true)}>
            <i className="icon-Delete" />
          </button>
        </CustomTooltip>
      </div>

      {showDeleteConfirm && (
        <ConfirmationPopup
          onOpen={showDeleteConfirm}
          onConfirm={deleteSelected}
          title={t('DELETE_CONFIRM.TITLE')}
          message={t('DELETE_CONFIRM.MESSAGE')}
          toggle={() => setShowDeleteConfirm(false)}
        />
      )}

      <GradingPushModal show={showGradingPopup} onConfirm={runPush} onCancel={() => setShowGradingPopup(false)} />
    </PageWrapper>
  );
};

export default MultiSubmit;

/**
 * True when a key event comes from a control inside a cell (input, select, multi-select, button) rather
 * than the selected cell itself. The control keeps every such key, so arrow keys in an open multi-select
 * never move the grid's cell focus.
 * @param target - the event target.
 * @returns whether the target is anything other than the cell element.
 */
function isInsideCellControl(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && !target.classList.contains('ag-cell');
}

/**
 * True for keys that would open AG Grid's built-in text editor on a fillable cell. Cells edit through
 * their own controls, so those keys are kept from the grid; Ctrl/Cmd chords (copy, paste, undo) pass.
 * @param event - the key event.
 * @returns whether the grid should ignore the key.
 */
function isEditStartKey(event: KeyboardEvent): boolean {
  const { key, ctrlKey, metaKey, altKey } = event;
  if (ctrlKey || metaKey || altKey) return false;
  return key.length === 1 || EDIT_START_KEYS.has(key);
}

const EDIT_START_KEYS: ReadonlySet<string> = new Set(['Enter', 'F2', 'Backspace', 'Delete']);

/**
 * A row with a new Sample Type and the sample chain that type continues, as picking it does: the
 * candidate samples are those of the row's own Sample Type Group, and the supplier follows the chain
 * only when that supplier is one the page offers — otherwise the row keeps the supplier it had.
 * @param row - the row.
 * @param sampleTypeId - the chosen Sample Type.
 * @param rowOptions - the row's option lists, absent while they load.
 * @param supplierOptions - the suppliers the Request Sample Supplier cell offers.
 * @returns the updated row.
 */
function withSampleType(row: MultiSubmitRow, sampleTypeId: string, rowOptions: RowOptions | undefined, supplierOptions: SelectOption[]): MultiSubmitRow {
  const samplesList = rowOptions?.samplesList ?? [];
  const group = (rowOptions?.sampleTypeGroupRaw ?? []).find((sampleGroup) => sampleGroup._id === row.sampleTypeGroup);
  const scopedSamples = group && row.typeOfSampleType !== 'other' ? samplesOfGroup(group, samplesList) : samplesList;
  const sampleContext = resolveNextSampleContext(scopedSamples, sampleTypeId);
  const chainSupplierOffered = supplierOptions.some((option) => option.value === sampleContext.supplier);
  return {
    ...row,
    sampleType: sampleTypeId,
    rsSupplier: chainSupplierOffered ? sampleContext.supplier : row.rsSupplier || '',
    parentId: sampleContext.parentId,
    revisionRootId: sampleContext.revisionRootId,
    versionNumber: sampleContext.versionNumber,
    rsChecked: !!sampleTypeId || row.rsChecked,
  };
}
